/**
 * The fields of a set launch beside its data: the adapter a Qwen run
 * starts from, and the Advanced disclosure holding the preset values a
 * launch may change: the epochs and the learning rate (TRA-1345), the
 * nodes and the batch (TRA-1685), and on
 * a base model with an Automodel recipe the sequence length (TRA-1559), the
 * validation passes, the LoRA rank and alpha, the schedule and the
 * checkpoint sealed (TRA-1564).
 */

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { decimalValue, presetValue, sequenceSummary, sftAdvancedChanged, sftAdvancedLabel } from './launch'
import type {
  SftAdvanced,
  SftAdvancedField,
  SftAdvancedTyped,
  SftAutomodelFields,
  SftLrSchedule,
  SftParallelFields,
  SftSealedCheckpoint,
} from './launch'

interface FieldProps {
  value: string
  onChange: (value: string) => void
  pending: boolean
}

export function SftCheckpointField({ value, onChange, pending }: FieldProps): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor="sft-checkpoint">Starting adapter (optional)</Label>
      <Input id="sft-checkpoint" value={value} disabled={pending}
        onChange={(event) => { onChange(event.target.value) }}
        placeholder="training-runs/team/run/exports/adapters/step_4/"
        aria-describedby="sft-checkpoint-help" />
      <p id="sft-checkpoint-help" className="text-xs text-muted-foreground">
        Continue from a sealed Qwen adapter on the same base model. SFT starts a new optimizer and step count.
      </p>
    </div>
  )
}

/** What one Advanced field changes in what was typed. */
type SftAdvancedChange = (changes: Partial<SftAdvancedTyped>) => void

/**
 * The Advanced disclosure of a set launch: each value beside the default
 * its recipe trains at and the bounds the api checks, closed until someone
 * opens it. A value outside its bounds reads as the reason Run training is
 * off. Every other value in the recipe is fixed, so runs stay comparable.
 */
export function SftAdvancedSection({
  advanced,
  pending,
  onChange,
  onReset,
}: {
  advanced: SftAdvanced
  pending: boolean
  onChange: SftAdvancedChange
  onReset: () => void
}): React.JSX.Element {
  const { epochs, rate, parallel, automodel } = advanced
  const epochsBounds = `${String(epochs.option.minimum)} to ${String(epochs.option.maximum)}`
  return (
    <details className="border border-border p-3">
      <summary className="cursor-pointer font-mono text-xs uppercase tracking-wider text-muted-foreground">
        {sftAdvancedLabel(advanced)}
      </summary>
      <div className="mt-3 space-y-4">
        <NumberField
          id="sft-epochs"
          label="Epochs"
          hint={`Recipe default ${String(epochs.default)}`}
          field={epochs}
          inputMode="numeric"
          pending={pending}
          onChange={(text) => { onChange({ epochs: text }) }}
          help={`Enter a whole number from ${epochsBounds}.`}
          invalid={`Run training needs a whole number from ${epochsBounds}.`}
        />
        {rate === null ? (
          <div className="space-y-2">
            <Label htmlFor="sft-rate">Learning rate</Label>
            <Input
              id="sft-rate"
              className="font-mono text-xs"
              value="Recipe default"
              disabled
              readOnly
              aria-describedby="sft-rate-help"
            />
            <p id="sft-rate-help" className="text-xs text-muted-foreground">
              This stack cannot change it. {advanced.notice}
            </p>
          </div>
        ) : (
          <RateField rate={rate} pending={pending} onChange={(text) => { onChange({ rate: text }) }} />
        )}
        {parallel !== null && <ParallelFields fields={parallel} pending={pending} onChange={onChange} />}
        {automodel !== null && <AutomodelFields fields={automodel} pending={pending} onChange={onChange} />}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={pending || sftAdvancedChanged(advanced) === 0}
          onClick={onReset}
        >
          Reset to recipe defaults
        </Button>
        <p className="text-xs text-muted-foreground">
          Every other value in the recipe is fixed, so runs stay comparable.
        </p>
      </div>
    </details>
  )
}

/** A label with the default beside it, in the section's one layout. */
function FieldLabel({ id, label, hint }: { id: string; label: string; hint: string }): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <Label htmlFor={id}>{label}</Label>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{hint}</span>
    </div>
  )
}

/**
 * A typed Advanced field: its help while the value is within its bounds,
 * the reason Run training is off while it is not, and `off` in place of
 * both where the field cannot change for this set.
 */
function NumberField({
  id,
  label,
  hint,
  field,
  inputMode,
  pending,
  onChange,
  help,
  invalid,
  off = null,
}: {
  id: string
  label: string
  hint: string
  field: SftAdvancedField<unknown>
  inputMode: 'numeric' | 'decimal'
  pending: boolean
  onChange: (text: string) => void
  help: string
  invalid: string
  off?: string | null
}): React.JSX.Element {
  const valid = field.value !== null
  return (
    <div className="space-y-2">
      <FieldLabel id={id} label={label} hint={hint} />
      <Input
        id={id}
        inputMode={inputMode}
        className="font-mono text-xs"
        value={field.text}
        disabled={pending || off !== null}
        aria-invalid={!valid}
        aria-describedby={`${id}-help`}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
      <p id={`${id}-help`} className={valid ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
        {off ?? (valid ? help : invalid)}
      </p>
    </div>
  )
}

function RateField({
  rate,
  pending,
  onChange,
}: {
  rate: NonNullable<SftAdvanced['rate']>
  pending: boolean
  onChange: (text: string) => void
}): React.JSX.Element {
  const bounds = `${presetValue(rate.option.minimum)} to ${presetValue(rate.option.maximum)}`
  return (
    <NumberField
      id="sft-rate"
      label="Learning rate"
      hint={`Recipe default ${presetValue(rate.default)}`}
      field={rate}
      inputMode="decimal"
      pending={pending}
      onChange={onChange}
      help={`Enter a rate from ${bounds} as ${presetValue(rate.default)} or ${decimalValue(rate.default)}.`}
      invalid={`Run training needs a rate from ${bounds}.`}
    />
  )
}

/** The nodes a run trains on and its batch (TRA-1685). */
function ParallelFields({
  fields,
  pending,
  onChange,
}: {
  fields: SftParallelFields
  pending: boolean
  onChange: SftAdvancedChange
}): React.JSX.Element {
  const { nodes, batch } = fields
  const copies = batch.option.copies
  return (
    <>
      <div className="space-y-2">
        <FieldLabel id="sft-nodes" label="Nodes" hint="Recipe default 1" />
        <Select
          value={String(nodes.value)}
          onValueChange={(value) => { onChange({ nodes: Number(value) }) }}
          disabled={pending || nodes.choices.length < 2}
        >
          <SelectTrigger id="sft-nodes" className="font-mono text-xs" aria-describedby="sft-nodes-help">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {nodes.choices.map((choice) => (
              <SelectItem key={choice} value={String(choice)} className="font-mono text-xs">
                {choice} × 8 GPUs
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p id="sft-nodes-help" className="text-xs text-muted-foreground">
          {nodes.choices.length < 2
            ? 'This target trains a run on one node.'
            : 'More nodes train more copies of the model at once, each on its share of the batch.'}
        </p>
      </div>
      <NumberField
        id="sft-batch"
        label="Batch size"
        hint={`Default ${String(batch.default)} on ${String(nodes.value)} node${nodes.value === 1 ? '' : 's'}`}
        field={batch}
        inputMode="numeric"
        pending={pending}
        onChange={(text) => { onChange({ batch: text }) }}
        help={`Enter a multiple of ${String(copies)} so each copy of the model gets an equal share.`}
        invalid={`Run training needs a whole multiple of ${String(copies)}.`}
      />
    </>
  )
}

const SCHEDULE_LABEL: Record<SftLrSchedule, string> = { constant: 'Constant', cosine: 'Cosine' }
const SEAL_LABEL: Record<SftSealedCheckpoint, string> = { last: 'Last', best: 'Best validation' }

/** The fields only a base model with an Automodel recipe offers (TRA-1564). */
function AutomodelFields({
  fields,
  pending,
  onChange,
}: {
  fields: SftAutomodelFields
  pending: boolean
  onChange: SftAdvancedChange
}): React.JSX.Element {
  const { passes, alpha, warmup, rank } = fields
  const passesBounds = `${String(passes.option.minimum)} to ${String(passes.option.maximum)}`
  const alphaBounds = `${String(alpha.option.minimum)} to ${String(alpha.option.maximum)} at rank ${String(rank.value)}`
  const warmupBounds = `${String(warmup.option.minimum)} to ${String(warmup.option.maximum)}`
  return (
    <>
      <SequenceLengthField fields={fields} pending={pending} onChange={onChange} />
      <NumberField
        id="sft-passes"
        label="Validation passes"
        hint={`Recipe default ${String(passes.default)}`}
        field={passes}
        inputMode="numeric"
        pending={pending}
        onChange={(text) => { onChange({ passes: text }) }}
        help={`Enter a whole number from ${passesBounds}. The run also validates before its first step and at each checkpoint.`}
        invalid={`Run training needs a whole number from ${passesBounds}.`}
        off={fields.skipsValidation}
      />
      <LoraRankField fields={fields} pending={pending} onChange={onChange} />
      <NumberField
        id="sft-alpha"
        label="LoRA alpha"
        hint={`Default ${String(alpha.default)} (${String(alpha.default / rank.value)} × rank)`}
        field={alpha}
        inputMode="numeric"
        pending={pending}
        onChange={(text) => { onChange({ alpha: text }) }}
        help={`Enter a whole number from ${alphaBounds}.`}
        invalid={`Run training needs a whole number from ${alphaBounds}.`}
      />
      <ScheduleField fields={fields} pending={pending} onChange={onChange} />
      <NumberField
        id="sft-warmup"
        label="Warmup"
        hint={`Recipe default ${String(warmup.default)}`}
        field={warmup}
        inputMode="decimal"
        pending={pending}
        onChange={(text) => { onChange({ warmup: text }) }}
        help={`Enter a share of the run's steps from ${warmupBounds}. The node rounds it to whole steps.`}
        invalid={`Run training needs a share from ${warmupBounds}.`}
      />
      <SealField fields={fields} pending={pending} onChange={onChange} />
    </>
  )
}

/**
 * The least length a run trains at: the one the picked set's rows need,
 * or a longer one the base model allows, each with the GPUs that share a
 * sequence there (TRA-1559).
 */
function SequenceLengthField({
  fields,
  pending,
  onChange,
}: {
  fields: SftAutomodelFields
  pending: boolean
  onChange: SftAdvancedChange
}): React.JSX.Element {
  const { length } = fields
  return (
    <div className="space-y-2">
      <FieldLabel id="sft-length" label="Sequence length" hint={`Rows need ${String(length.default)}`} />
      <Select
        value={String(length.value)}
        onValueChange={(value) => { onChange({ length: Number(value) }) }}
        disabled={pending || length.choices.length < 2}
      >
        <SelectTrigger id="sft-length" className="font-mono text-xs" aria-describedby="sft-length-help">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {length.choices.map((choice) => (
            <SelectItem key={choice.tokens} value={String(choice.tokens)} className="font-mono text-xs">
              {sequenceSummary(choice.tokens, choice.gpus_per_sequence)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id="sft-length-help" className="text-xs text-muted-foreground">
        The run trains at the longer of this and the length the rows need. A longer sequence is split over more
        GPUs, so a step trains fewer of them.
      </p>
    </div>
  )
}

function LoraRankField({
  fields,
  pending,
  onChange,
}: {
  fields: SftAutomodelFields
  pending: boolean
  onChange: SftAdvancedChange
}): React.JSX.Element {
  const { rank } = fields
  return (
    <div className="space-y-2">
      <FieldLabel id="sft-rank" label="LoRA rank" hint={`Recipe default ${String(rank.default)}`} />
      <Select
        value={String(rank.value)}
        onValueChange={(value) => { onChange({ rank: Number(value) }) }}
        disabled={pending}
      >
        <SelectTrigger id="sft-rank" className="font-mono text-xs" aria-describedby="sft-rank-help">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {rank.choices.map((choice) => (
            <SelectItem key={choice} value={String(choice)} className="font-mono text-xs">
              {choice}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id="sft-rank-help" className="text-xs text-muted-foreground">
        The adapter and its optimizer state grow with the rank.
      </p>
    </div>
  )
}

function ScheduleField({
  fields,
  pending,
  onChange,
}: {
  fields: SftAutomodelFields
  pending: boolean
  onChange: SftAdvancedChange
}): React.JSX.Element {
  const { schedule } = fields
  const help =
    schedule.value === 'cosine'
      ? `The rate decays to ${presetValue(fields.minLr)} by the last step, or to the same share of a changed rate.`
      : 'The rate stays constant after any warmup.'
  return (
    <div className="space-y-2">
      <FieldLabel id="sft-schedule" label="Learning-rate schedule" hint={`Recipe default ${schedule.default}`} />
      <Select
        value={schedule.value}
        onValueChange={(value) => { onChange({ schedule: schedule.choices.find((choice) => choice === value) ?? null }) }}
        disabled={pending}
      >
        <SelectTrigger id="sft-schedule" className="font-mono text-xs" aria-describedby="sft-schedule-help">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {schedule.choices.map((choice) => (
            <SelectItem key={choice} value={choice} className="font-mono text-xs">
              {SCHEDULE_LABEL[choice]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id="sft-schedule-help" className="text-xs text-muted-foreground">
        {help}
      </p>
    </div>
  )
}

function SealField({
  fields,
  pending,
  onChange,
}: {
  fields: SftAutomodelFields
  pending: boolean
  onChange: SftAdvancedChange
}): React.JSX.Element {
  const { seal, skipsValidation } = fields
  const help =
    seal.value === 'best'
      ? `The run saves a checkpoint at every validation pass and seals the one with the lowest validation loss. Each checkpoint takes about ${String(fields.checkpointSeconds)} s.`
      : 'The run seals the checkpoint of its last step.'
  return (
    <div className="space-y-2">
      <FieldLabel id="sft-seal" label="Sealed checkpoint" hint={`Default ${SEAL_LABEL[seal.default].toLowerCase()}`} />
      <Select
        value={seal.value}
        onValueChange={(value) => { onChange({ seal: seal.choices.find((choice) => choice === value) ?? null }) }}
        disabled={pending || skipsValidation !== null}
      >
        <SelectTrigger id="sft-seal" className="font-mono text-xs" aria-describedby="sft-seal-help">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {seal.choices.map((choice) => (
            <SelectItem key={choice} value={choice} className="font-mono text-xs">
              {SEAL_LABEL[choice]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id="sft-seal-help" className="text-xs text-muted-foreground">
        {skipsValidation ?? help}
      </p>
    </div>
  )
}
